﻿namespace Illy.SafeStack.Client

open System

type DateExpression =
    | Today
    | DayOfMonth of int
    | DayDiff of int
    | WeekDiff of int
    | MonthDiff of int
    | YearDiff of int

[<RequireQualifiedAccess>]
type ParsedDate = 
    | Empty
    | Valid of DateTime
    | BelowMin of string
    | Invalid of string

[<RequireQualifiedAccess>]
module DateExpression =
    let apply =
        function
        | Today -> DateTime.Today
        | DayOfMonth i -> 
            let today = DateTime.Today
            new DateTime(today.Year, today.Month, i)
        | DayDiff i -> DateTime.Today.AddDays(float i)
        | WeekDiff i -> DateTime.Today.AddDays(float (7*i))
        | MonthDiff i -> DateTime.Today.AddMonths(i)
        | YearDiff i -> DateTime.Today.AddYears(i)

    type private ParseContext =
        | Start
        | Sign
        | Modifier of isNegative: bool * diff: int
        | Specifier of isNegative: bool * diff: int * spec: Char
        | DayOfMonth of day: int

    let private ctxToDate =
        function
        | Start
        | Sign 
        | Modifier (_,0) -> Today |> Some
        | DayOfMonth i ->
            let today = DateTime.Today
            let daysInMonth = DateTime.DaysInMonth(today.Year, today.Month)
            if i > daysInMonth then None
            else (DateExpression.DayOfMonth i) |> Some
        | Modifier (neg,diff) -> DayDiff(if neg then -diff else diff) |> Some
        | Specifier (neg, diff, c) ->
            let diff2 = if neg then -diff else diff
            match c with
            | 'd' -> DayDiff diff2 |> Some
            | 'w' -> WeekDiff diff2 |> Some
            | 'm' -> MonthDiff diff2 |> Some
            | 'y' -> YearDiff diff2 |> Some
            | _ -> None

    let parse (str: string) =
        let rec loop ctx =
            function
            | [] -> ctxToDate ctx
            | H::T ->
                match ctx with
                | Start ->
                    match H with
                    | 't' -> loop Sign T
                    | '-' -> loop (Modifier(true, 0)) T
                    | '+' -> loop (Modifier(false, 0)) T
                    | c when Char.IsDigit c -> loop (DayOfMonth(Int32.Parse(c.ToString())) ) T
                    | _ -> None
                | DayOfMonth num -> 
                    match H with
                    | c when Char.IsDigit c -> 
                        let newNum = Int32.Parse(num.ToString() + c.ToString())
                        loop (DayOfMonth(newNum)) T
                    | _ -> None
                | Sign ->
                    match H with
                    | '-' -> loop (Modifier(true, 0)) T
                    | '+' -> loop (Modifier(false, 0)) T
                    | _ -> None
                | Modifier (neg, num) ->
                    if Char.IsDigit H then
                        let s = Int32.Parse(num.ToString() + H.ToString())
                        loop (Modifier(neg, s)) T
                    else
                        match H with
                        | 'd' 
                        | 'w'
                        | 'm'
                        | 'y' -> ctxToDate (Specifier(neg, num, H))
                        | _ -> None
                | Specifier(neg, num, c) ->
                    ctxToDate (Specifier(neg, num, c))
                        
        let trimmed = str.Trim().ToLowerInvariant()
        loop Start (trimmed.ToCharArray() |> List.ofArray)

module ParsedDate =
    open System
    open System.Text.RegularExpressions

    let minDate = new DateTime(1900, 1, 1)
    let regex = Regex("""^(\d{1,2})/(0{0,1}[1-9]|1[0-2]|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/(\d{4}|\d{2})$""", RegexOptions.IgnoreCase)
    
    let formatError =
        function
        | ParsedDate.BelowMin _ -> "Minimum date allowed is 01/Jan/1900."
        | ParsedDate.Invalid str -> sprintf "\"%s\" is not valid (e.g. 21/Apr/2018)." str
        | _ -> ""

    let toOption =
        function
        | ParsedDate.Valid d -> 
            //Reconstruct the date to avoid daylight saving serialisation issues
            new DateTime(d.Year, d.Month, d.Day) |> Some
        | _ -> None

    let fromOption =
        function
        | None -> ParsedDate.Empty
        | Some x -> ParsedDate.Valid x
    
    let private (|MonthInt|_|) (str: string) = 
        match Int32.TryParse str with
        | true, x when x > 0 && x <= 12 -> Some x
        | _ , _ -> None
        
    let private (|Month|_|) (str: string) = 
        match str.ToLowerInvariant() with
        | "jan" -> Some 1
        | "feb" -> Some 2
        | "mar" -> Some 3
        | "apr" -> Some 4
        | "may" -> Some 5
        | "jun" -> Some 6
        | "jul" -> Some 7
        | "aug" -> Some 8
        | "sep" -> Some 9
        | "oct" -> Some 10
        | "nov" -> Some 11
        | "dec" -> Some 12
        | _ -> None
    
    let private parseDay (day: string) month year dateString= 
        let daysInMonth = DateTime.DaysInMonth(year, month)
    
        match Int32.TryParse day with
        | true, v when v > 0 && v <= daysInMonth ->
            ParsedDate.Valid (new DateTime(year, month, v))
        | _, _ -> ParsedDate.Invalid dateString

    let private parseMonth day month year dateString = 
        match month with
        | Month m 
        | MonthInt m -> 
            parseDay day m year dateString
        | _ -> ParsedDate.Invalid dateString

    let private expandTwoDigitYear year =
        if year > 99 then
            year
        else if year < 50 then 2000 + year 
        else 1900 + year

    let private parseYear day month (year: string) dateString =
        match Int32.TryParse(year) with
        | true, x ->
            let expanded = expandTwoDigitYear x
            if expanded < minDate.Year then
                ParsedDate.BelowMin dateString
            else
                parseMonth day month expanded dateString   
        | _ , _ -> 
            ParsedDate.Invalid dateString

    let private matchDate input = 
        let res = regex.Match(input)
    
        if not res.Success then 
            ParsedDate.Invalid input
        else

            if res.Groups.Count <> 4 then 
                ParsedDate.Invalid input
            else
                let group0 = res.Groups.Item 1
                let group1 = res.Groups.Item 2
                let group2 = res.Groups.Item 3
                parseYear group0.Value group1.Value group2.Value input

    let ofString str = 
        if String.IsNullOrWhiteSpace str then 
            (ParsedDate.Empty, None)
        else  
            match DateExpression.parse str with
            | None ->
                match matchDate str with 
                | ParsedDate.Empty -> (ParsedDate.Empty, None)
                | ParsedDate.Valid d -> (ParsedDate.Valid d, None)
                | ParsedDate.BelowMin str' -> (ParsedDate.BelowMin str', ParsedDate.BelowMin str' |> formatError |> Some)
                | ParsedDate.Invalid str' -> (ParsedDate.Invalid str', ParsedDate.Invalid str' |> formatError |> Some)
            | Some expr ->
                let date = DateExpression.apply expr
                if date < minDate then 
                    (ParsedDate.BelowMin str, ParsedDate.BelowMin str |> formatError |> Some)
                else
                    (ParsedDate.Valid date, None)
    
    let toString formatter = 
        function
        | ParsedDate.Empty -> ""
        | ParsedDate.Valid d -> formatter d
        | ParsedDate.Invalid str -> str 
        | ParsedDate.BelowMin str -> str

    let formatToOption formatter = 
        function
        | ParsedDate.Empty -> None
        | ParsedDate.Valid d -> Some <| formatter d
        | ParsedDate.Invalid str 
        | ParsedDate.BelowMin str -> Some str